import { apiClient, ApiResponse } from './apiClient';

// User Management interfaces matching API structure
export interface RoleDetails {
  id: number;
  name: string;
  description: string;
}

export interface UserManagementUser {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  language: 'en' | 'ar';
  roleId: number; // 1 = customer, 2 = admin, 3 = staff
  status: 'active' | 'inactive' | 'suspended' | 'deleted';
  last_login_at: string | null;
  createdAt: string;
  updatedAt: string;
  role: string; // 'customer', 'admin', 'staff'
  roleDetails: RoleDetails;
  // Optional fields for UI
  password?: string;
  token?: string;
}

export interface UserSearchFilters {
  page?: number; // Required, default: 1
  limit?: number; // Required, default: 10
  search?: string; // Optional: Search by name, phone, or email
  roleId?: string; // Optional: Filter by role ID (1, 2, or 3)
  status?: string; // Optional: Filter by status (active, suspended, deleted)
}

export interface PaginationResponse {
  total: number;
  totalPages: number;
  currentPage: number;
  perPage: number;
}

export interface UsersResponse {
  users: UserManagementUser[];
  pagination: PaginationResponse;
}

export interface UserListResponse {
  users: UserManagementUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Helper function to transform API user to display user
function transformUser(apiUser: UserManagementUser): UserManagementUser {
  // Map roleId to role string
  const roleMap: { [key: number]: string } = {
    1: 'customer',
    2: 'admin', 
    3: 'staff'
  };

  return {
    ...apiUser,
    // Map roleId to role string for display
    role: roleMap[apiUser.roleId] as any || 'customer',
    // Use createdAt as registrationDate
    registrationDate: apiUser.createdAt,
    // Use updatedAt as lastLogin (approximation)
    lastLogin: apiUser.updatedAt,
    // Default status to active (you can enhance this based on your logic)
    status: 'active',
    // Remove sensitive fields for display
    password: undefined,
    token: undefined
  };
}

export interface CreateUserRequest {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  status?: string;
  language?: 'en' | 'ar';
}

export interface UpdateUserRequest {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  status?: string;
  language?: 'en' | 'ar';
}

export interface UserActivity {
  id: string;
  userId: string;
  actionType: string;
  actionDetails: any;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
}

export interface UserStats {
  total: number;
  active: number;
  inactive: number;
  blocked: number;
  admins: number;
  staff: number;
  customers: number;
}

// User Management Service Class
export class UserManagementService {
  private baseUrl = '/admin/users';
  private authUrl = '/auth';

  // Get users with search and filters (matches GET /admin/users)
  async getUsers(filters: UserSearchFilters = {}): Promise<ApiResponse<UsersResponse>> {
    const params = new URLSearchParams();
    
    // Required parameters with defaults
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Optional parameters - only add if provided
    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.roleId) {
      params.append('roleId', filters.roleId);
    }
    if (filters.status) {
      params.append('status', filters.status);
    }

    const queryString = params.toString();
    const url = `${this.baseUrl}?${queryString}`;
    
    console.log('🔧 UserManagementService.getUsers:', { url, filters });
    
    try {
      // API returns: { success: true, message: "...", data: { users: [...], pagination: {...} } }
      const response = await apiClient.get<UsersResponse>(url);
      
      console.log('🔧 UserManagementService.getUsers response:', response);
      
      if (response.success && response.data) {
        // Response data should already contain { users, pagination }
        return {
          success: true,
          data: response.data,
          message: response.message || 'Users fetched successfully'
        };
      }
      
      return {
        success: false,
        data: { 
          users: [], 
          pagination: {
            total: 0,
            totalPages: 0,
            currentPage: 1,
            perPage: limit
          }
        },
        message: response.message || 'Failed to fetch users'
      };
    } catch (error: any) {
      console.error('❌ Error fetching users:', error);
      return {
        success: false,
        data: { 
          users: [], 
          pagination: {
            total: 0,
            totalPages: 0,
            currentPage: 1,
            perPage: limit
          }
        },
        message: error?.message || 'Error fetching users'
      };
    }
  }

  // Get user by ID (matches your GET /admin/users/{id})
  async getUserById(id: string): Promise<ApiResponse<UserManagementUser>> {
    try {
      const response = await apiClient.get<UserManagementUser>(`${this.baseUrl}/${id}`);
      
      if (response.success && response.data) {
        const transformedUser = transformUser(response.data);
        return {
          ...response,
          data: transformedUser
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error fetching user'
      };
    }
  }

  // Create new user using auth registration endpoints
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<UserManagementUser>> {
    // Use appropriate registration endpoint based on role
    let endpoint = `${this.authUrl}/register`;
    
    switch (userData.role) {
      case 'staff':
        endpoint = `${this.authUrl}/register/staff`;
        break;
      case 'admin':
        endpoint = `${this.authUrl}/register/admin`;
        break;
      case 'customer':
      default:
        endpoint = `${this.authUrl}/register/customer`;
        break;
    }
    
    return apiClient.post<UserManagementUser>(endpoint, userData);
  }

  // Update user (matches your PUT /admin/users/{id})
  async updateUser(id: string, userData: UpdateUserRequest): Promise<ApiResponse<UserManagementUser>> {
    try {
      // Convert role string back to roleId if provided
      const requestData: any = { ...userData };
      
      if (userData.role) {
        const roleIdMap: { [key: string]: number } = {
          'customer': 1,
          'admin': 2,
          'staff': 3
        };
        requestData.roleId = roleIdMap[userData.role];
        delete requestData.role; // Remove role string, send roleId
      }
      
      const response = await apiClient.patch<UserManagementUser>(`${this.baseUrl}/${id}`, requestData);
      
      if (response.success && response.data) {
        const transformedUser = transformUser(response.data);
        return {
          ...response,
          data: transformedUser
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error updating user:', error);
      return {
        success: false,
        data: undefined,
        message: 'Error updating user'
      };
    }
  }

  // Delete user (DELETE /admin/users/{id})
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Update user status (PUT /admin/users/{id}/status)
  async updateUserStatus(id: string, status: 'active' | 'inactive'): Promise<ApiResponse<UserManagementUser>> {
    return apiClient.put<UserManagementUser>(`${this.baseUrl}/${id}/status`, { 
      status 
    });
  }

  // Request password reset OTP (POST /auth/request-reset)
  async requestPasswordReset(phone: string, sendViaWhatsapp: boolean = false): Promise<ApiResponse<{
    otp: string;
    expiresAt: string;
    sentVia: string;
  }>> {
    return apiClient.post<{
      otp: string;
      expiresAt: string;
      sentVia: string;
    }>(`${this.authUrl}/request-reset`, { 
      phone,
      sendViaWhatsapp 
    });
  }

  // Reset password with OTP (POST /auth/reset-password)
  async resetPassword(phone: string, otp: string, newPassword: string, confirmPassword: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.post<{ message: string }>(`${this.authUrl}/reset-password`, { 
      phone,
      otp,
      newPassword,
      confirmPassword
    });
  }

  // Bulk operations (will be implemented later or use individual calls)
  async bulkUpdateUsers(userIds: string[], action: string, data?: any): Promise<ApiResponse<void>> {
    // For now, use individual API calls for each user
    // This can be optimized later when bulk endpoint is available
    const promises = userIds.map(userId => {
      switch (action) {
        case 'activate':
          return this.updateUserStatus(userId, 'active');
        case 'block':
        case 'suspend':
          return this.updateUserStatus(userId, 'inactive');
        case 'delete':
          return this.deleteUser(userId);
        default:
          return Promise.resolve({ success: true, data: null });
      }
    });

    await Promise.all(promises);
    return { success: true, data: undefined, message: 'Bulk operation completed' };
  }

  // Get user statistics (calculated from user list)
  async getUserStats(): Promise<ApiResponse<UserStats>> {
    try {
      const response = await this.getUsers({ page: 1, pageSize: 1000 }); // Get all users
      if (response.success && response.data) {
        const users = response.data.users;
        const stats: UserStats = {
          total: users.length,
          active: users.filter(u => u.status === 'active').length,
          inactive: users.filter(u => u.status === 'inactive').length,
          blocked: users.filter(u => u.status === 'blocked').length,
          admins: users.filter(u => u.role === 'admin' || u.role === 'super_admin').length,
          staff: users.filter(u => u.role === 'staff').length,
          customers: users.filter(u => u.role === 'customer').length,
        };
        return { success: true, data: stats, message: 'Stats calculated' };
      }
      throw new Error('Failed to fetch users for stats');
    } catch (error) {
      // Return default stats if calculation fails
      return { 
        success: true, 
        data: { total: 0, active: 0, inactive: 0, blocked: 0, admins: 0, staff: 0, customers: 0 }, 
        message: 'Default stats' 
      };
    }
  }

  // Get user activity logs (placeholder - will need activity endpoint)
  async getUserActivities(userId: string, page: number = 1, pageSize: number = 25): Promise<ApiResponse<UserActivity[]>> {
    // This endpoint doesn't exist yet, return empty array
    return { 
      success: true, 
      data: [], 
      message: 'Activity logs not available yet' 
    };
  }

  // Export users (placeholder - will need export endpoint)
  async exportUsers(filters: UserSearchFilters = {}, format: 'csv' | 'excel' = 'csv'): Promise<ApiResponse<{ downloadUrl: string }>> {
    // This endpoint doesn't exist yet, return mock response
    return { 
      success: false, 
      data: undefined, 
      message: 'Export functionality not available yet' 
    };
  }

  // Import users (placeholder - will need import endpoint)
  async importUsers(file: File): Promise<ApiResponse<{ imported: number; errors: any[] }>> {
    // This endpoint doesn't exist yet, return mock response
    return { 
      success: false, 
      data: undefined, 
      message: 'Import functionality not available yet' 
    };
  }

  // Role management (placeholder - will need role endpoints)
  async getRoles(): Promise<ApiResponse<{ id: string; name: string; permissions: string[] }[]>> {
    // Return default roles for now
    const defaultRoles = [
      { id: 'customer', name: 'Customer', permissions: ['view_profile'] },
      { id: 'staff', name: 'Staff', permissions: ['view_bookings', 'manage_bookings'] },
      { id: 'admin', name: 'Admin', permissions: ['view_all', 'manage_users', 'manage_bookings'] },
      { id: 'super_admin', name: 'Super Admin', permissions: ['full_access'] }
    ];
    
    return { success: true, data: defaultRoles, message: 'Default roles' };
  }

  // Assign role to user (done via update user)
  async assignRole(userId: string, roleId: string): Promise<ApiResponse<UserManagementUser>> {
    return this.updateUser(userId, { role: roleId as any });
  }

  // Remove role from user (set to customer)
  async removeRole(userId: string, roleId: string): Promise<ApiResponse<UserManagementUser>> {
    return this.updateUser(userId, { role: 'customer' });
  }
}

// Export singleton instance
export const userManagementService = new UserManagementService();
export default userManagementService;
